import { useState } from 'react'
import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import { Alert, InputV2 as Input, Button, space, Checkbox } from '@ticketswap/solar'
import { string, object } from 'yup'
import { useMutation } from '@apollo/client'
import { useFormik } from 'formik'
import { AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import { track, setTrackingIdentity } from '~/utils/analytics'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { GIVE_USER_CONSENT } from '~/graphql/mutations/giveUserConsent'
import { REGISTER_USER } from '~/graphql/mutations/registerUserUsingEmail'
import getConfig from 'next/config'
import { setCookie } from 'cookies-next'
import { MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const InputWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${space[8]};
`

const StyledCheckbox = styled(Checkbox)`
  margin-block: ${space[16]};
  margin-inline: 0;
`

const StyledAlert = styled(Alert)`
  margin-block-end: ${space[8]};
`

const validationSchema = object().shape({
  firstname: string().required(),
  lastname: string().required(),
})

const SignupEmail = ({ t, onSignupComplete }) => {
  const [error, setError] = useState(null)
  const [userLoading, setUserLoading] = useState(false)
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false)
  const [giveConsentMutation, { client }] = useMutation(GIVE_USER_CONSENT)
  const [generate, { loading: generating }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [register, { loading }] = useMutation(REGISTER_USER)
  const { email } = useLoggedInUser()

  const handleSubmit = async submitted => {
    setError(null)

    const {
      data: {
        authenticateClientForToken: { token },
      },
    } = await submitted.generate({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const {
      data: { registerUserUsingEmail },
    } = await submitted.register({
      variables: {
        input: {
          ...submitted.values,
          redirectTo: window.location.href,
          token,
        },
      },
    })

    if (registerUserUsingEmail.errors.length === 0) {
      setUserLoading(true)

      const {
        apiAccessToken: { tokenString },
      } = registerUserUsingEmail

      setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
        maxAge: MONTH_IN_SECONDS,
        secure: true,
        sameSite: 'lax',
      })

      await client.resetStore()

      await setTrackingIdentity()

      if (marketingConsentGiven) {
        await giveConsentMutation({
          variables: {
            input: {
              consentType: 'MARKETING_EMAIL',
              hasGivenConsent: true,
            },
          },
        })
      }

      track(AUTHENTICATION_COMPLETED, {
        method: 'email',
        isNew: true,
        withEmailVerify: true,
      })

      onSignupComplete('email', true)
    } else {
      setError(registerUserUsingEmail.errors[0].message)

      track(AUTHENTICATION_COMPLETED, {
        method: 'email',
        isNew: true,
        error: registerUserUsingEmail.errors[0].code,
      })
    }
  }

  const formik = useFormik({
    validationSchema,
    initialValues: {
      email,
      firstname: '',
      lastname: '',
    },
    onSubmit: async values => handleSubmit({ values, generate, register }),
  })

  const { handleChange } = formik

  return (
    <form onSubmit={formik.handleSubmit}>
      <InputWrapper>
        <Input
          label={t('common:auth.signupEmail.fields.firstname.label')}
          type="text"
          id="firstname"
          name="firstname"
          value={formik.values.firstname}
          onChange={handleChange}
        />

        <Input
          label={t('common:auth.signupEmail.fields.lastname.label')}
          type="text"
          id="lastname"
          name="lastname"
          value={formik.values.lastname}
          onChange={handleChange}
        />
      </InputWrapper>

      <StyledCheckbox
        id="marketingConsent"
        name="marketingConsent"
        label={t('common:auth.consent.label')}
        onChange={event => setMarketingConsentGiven(event.target.checked)}
      />
      {error && <StyledAlert variant="error">{error}</StyledAlert>}
      <Button
        variant="success"
        type="submit"
        fullWidth
        loading={generating || loading || userLoading}
        disabled={!formik.isValid}
      >
        {t('common:auth.signupEmail.button')}
      </Button>
    </form>
  )
}

SignupEmail.propTypes = {
  t: PropTypes.func.isRequired,
  onSignupComplete: PropTypes.func.isRequired,
}

export default SignupEmail
